import { useCallback, useMemo, useState, useEffect } from "react";
import { Button, Card, CardBody, CardHeader, Divider, Input, RadioGroup, ScrollShadow, Spinner, Tab, Tabs, ButtonGroup } from "@nextui-org/react";
import RootAdmin from "../../../../components/layouts/RootAdmin";
import useSWR from "swr";
import toast from "react-hot-toast";
import { getFetcher, deleteWithFormFetcherTest, putFetcherWithoutId } from "../../../../services/api";
// eslint-disable-next-line
import { EyeIcon, MagnifyingGlassIcon, PlusIcon } from "@heroicons/react/24/outline";
import useCustomNavigate from "../../../../hooks/useCustomNavigate";
import RadioVehicleButton from "../../../../components/fragments/admin/report/transport/RadioVehicleButton";
import { month } from "../../../../services/enum";
import IsValidIcon from "../../../../components/elements/isValidIcon";
import ModalGenerateReport from "../../../../components/fragments/admin/report/transport/ModalGenerateReport";
import FilterReactSelect from "../../../../components/elements/FilterReactSelect";
import ReportStatusBadge from "../../../../components/elements/ReportStatusBadge";
import TableHistoryTransport from "../../../../components/fragments/admin/report/transport/TableHistoryTransport";
import { hasValidRole } from "../../../../services/helpers";
import { getErrorMessage } from "../../../../services/errorMessage";
import useAuth from "../../../../hooks/useAuth";
import RolesAccess from "../../../../enums/roles";
import TableReportTransportPreview from "../../../../components/fragments/admin/report/preview/TableReportTransportPreview";
import { useSelectedCompany } from "../../../../hooks/useSelectedCompany";
import useActivePeriodSelection from "../../../../hooks/useActivePeriodSelection";
import useListCompany from "../../../../hooks/useListCompany";
import { useNavigate } from "react-router-dom";
// eslint-disable-next-line
import { REPORT_TRANSPORT_RECOMENDATION_VEHICLE_ADD } from "../../../../services/routes";
import ClientDataGrid from "../../../../components/elements/ClientDataGrid";

// =====================================================================
// ENDPOINT
//
// Hanya aksi hapus, bekerja di level KENDARAAN. Satu kendaraan punya satu baris
// pelaporan per bulan, jadi menghapus lewat loop dari frontend berisiko gagal
// separuh jalan. Lihat backend-additions.ts.
// =====================================================================
const deleteVehicleReportApi = (payload) => deleteWithFormFetcherTest("/api/pelaporan-pengangkutan/vehicle", payload);

// Menandai "tidak ada pengangkutan" menghapus detail SELURUH bulan kendaraan
// tersebut pada periode aktif, jadi dikerjakan satu transaksi di backend.
const setTanpaPengangkutanApi = (payload) => putFetcherWithoutId("/api/pelaporan-pengangkutan/vehicle/tanpa-pengangkutan", payload);

export default function VehiclePage() {
  const { roles } = useAuth();
  // eslint-disable-next-line
  const navigate = useNavigate();
  const [selectedCompany, setSelectedCompany] = useSelectedCompany();
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [vehicleQuery, setVehicleQuery] = useState("");
  const [savingNoTransportId, setSavingNoTransportId] = useState(null);
  const { getReportTransportVehicleDetail } = useCustomNavigate();
  /*
   * Halaman ini khusus pelaporan PENGANGKUTAN, jadi daftar perusahaannya
   * disaring ke tipe pengangkut.
   *
   * Alasannya bukan sekadar relevansi: tanpa filter, endpoint perusahaan
   * mengembalikan seluruh 8.585 baris - 10,2 MB dan ~2,5 detik - hanya untuk
   * mengisi satu dropdown. Dengan filter ini menjadi 243 baris, 346 KB, ~130 ms.
   */
  const { dataCompany, isLoadingCompany } = useListCompany({ tipePerusahaan: ["Mengangkut B3"], hasIdCompanySimpel: true });

  /**
   * Periode default = periode aktif TERLAMA yang laporan pengangkutannya belum
   * selesai untuk perusahaan ini. Logikanya ada di hook bersama
   * useActivePeriodSelection, dipakai kelima modul pelaporan.
   */
  const { selectedPeriod, setSelectedPeriod, dataPeriods, isLoadingPeriods } = useActivePeriodSelection(
    selectedCompany?.value,
    "pelaporan-pengangkutan",
  );

  const {
    data: dataReport,
    isLoading: isLoadingReport,
    mutate: mutateReport,
  } = useSWR(selectedCompany?.value && selectedPeriod?.value ? `/api/pelaporan-pengangkutan/search?returnAll=true&companyId=${selectedCompany.value}&periodId=${selectedPeriod.value}` : null, getFetcher);

  const isAdminOrPengelola = hasValidRole(roles, [RolesAccess.PENGELOLA, RolesAccess.SUPER_ADMIN]);

  /*
   * Daftar kendaraan diambil dari MASTER kendaraan perusahaan, bukan disimpulkan
   * dari baris pelaporan.
   *
   * Sebelumnya isinya hasil groupedVehicle(dataReport) - hanya kendaraan yang
   * sudah punya baris pelaporan pada periode terpilih. Kendaraan yang belum
   * di-generate sama sekali tidak terlihat, padahal justru itu yang perlu
   * dilaporkan. Contoh nyata: satu perusahaan punya 127 kendaraan tapi hanya 3
   * yang muncul.
   *
   * Kuncinya perusahaan saja, bukan periode - kepemilikan kendaraan tidak
   * berubah antar periode.
   */
  const { data: dataKendaraan, isLoading: isLoadingKendaraan } = useSWR(
    selectedCompany?.value ? `/api/vehicles/search?returnAll=true&companyId=${selectedCompany.value}` : null,
    getFetcher,
  );

  const vehicleData = dataKendaraan?.vehicles ?? [];

  // Filter nomor polisi. Dicocokkan tanpa memedulikan huruf besar/kecil dan
  // spasi, karena nomor polisi lazim ditulis "B 9147 UEN" tapi dicari "b9147".
  const normalisasiPlat = (nilai) => (nilai || "").toString().toUpperCase().replace(/\s+/g, "");
  const kataKunciPlat = normalisasiPlat(vehicleQuery);
  const vehicleDataTersaring = kataKunciPlat ? vehicleData.filter((item) => normalisasiPlat(item?.noPolisi).includes(kataKunciPlat)) : vehicleData;

  // Dipakai bersama oleh tabel dan aksi hapus supaya keduanya tidak mungkin
  // memberi hasil yang berbeda.
  const getRowsForVehicle = useCallback(
    (vehicleId) => {
      const rows = dataReport?.data ?? [];
      if (!vehicleId) return [];
      return rows.filter((item) => item.vehicleId === vehicleId);
    },
    [dataReport?.data],
  );

  const filteredData = selectedVehicle && !isLoadingReport && dataReport ? getRowsForVehicle(selectedVehicle) : [];

  // Hapus seluruh baris pelaporan kendaraan tsb, lalu bersihkan pilihan aktif.
  const handleDeleteVehicle = useCallback(
    async (vehicle) => {
      if (getRowsForVehicle(vehicle?.id).length === 0) {
        throw new Error("Tidak ada baris pelaporan untuk kendaraan ini");
      }

      await deleteVehicleReportApi({
        companyId: selectedCompany?.value,
        periodId: selectedPeriod?.value,
        vehicleId: vehicle?.id,
      });

      if (selectedVehicle === vehicle?.id) setSelectedVehicle(null);

      await mutateReport();
    },
    [getRowsForVehicle, mutateReport, selectedCompany?.value, selectedPeriod?.value, selectedVehicle],
  );

  /**
   * Penanda "tidak ada pengangkutan" disimpan di kolom laporanTersedia milik
   * PelaporanPengangkutan - tercentang berarti laporanTersedia = false.
   *
   * Kartu kendaraan mewakili SATU PERIODE, sedangkan satu kendaraan punya satu
   * baris pelaporan per bulan. Jadi penanda ini diterapkan ke seluruh baris
   * bulan kendaraan tersebut dalam periode terpilih - sama cakupannya dengan
   * aksi hapus pada kartu yang sama.
   */
  const isNoTransport = useCallback(
    (vehicleId) => {
      const rows = getRowsForVehicle(vehicleId);
      return rows.length > 0 && rows.every((row) => row.laporanTersedia === false);
    },
    [getRowsForVehicle],
  );

  const handleToggleNoTransport = useCallback(
    async (vehicle, checked) => {
      const rows = getRowsForVehicle(vehicle?.id);
      if (rows.length === 0) {
        toast.error("Tidak ada baris pelaporan untuk kendaraan ini");
        return;
      }

      setSavingNoTransportId(vehicle?.id);
      try {
        // Satu panggilan transaksional, bukan loop per bulan: mencentang berarti
        // menghapus detail seluruh bulan, dan penghapusan separuh jalan akan
        // menyisakan data yang tidak konsisten.
        const respons = await setTanpaPengangkutanApi({
          companyId: selectedCompany?.value,
          periodId: selectedPeriod?.value,
          vehicleId: vehicle?.id,
          tanpaPengangkutan: checked,
        });

        toast.success(respons?.message || (checked ? "Ditandai tidak ada pengangkutan" : "Penanda tidak ada pengangkutan dilepas"));
        await mutateReport();
      } catch (error) {
        toast.error(getErrorMessage(error, "Gagal menyimpan penanda tidak ada pengangkutan"));
      } finally {
        setSavingNoTransportId(null);
      }
    },
    [getRowsForVehicle, mutateReport, selectedCompany?.value, selectedPeriod?.value],
  );

  // Kosongkan kendaraan terpilih saat perusahaan atau periode berganti.
  //
  // mutateReport() sengaja TIDAK dipanggil di sini: companyId dan periodId sudah
  // menjadi bagian dari key SWR, jadi SWR otomatis fetch ulang saat keduanya
  // berubah. Memanggil mutate lagi hanya menghasilkan request kedua.
  //
  // Dependensinya memakai nilai primitif, bukan objek selectedCompany /
  // selectedPeriod, supaya efek ini tidak berjalan ulang hanya karena
  // referensi objeknya berganti.
  useEffect(() => {
    setSelectedVehicle(null);
  }, [selectedCompany?.value, selectedPeriod?.value]);

  const columns = useMemo(
    () => [
      { field: "period", headerName: "Periode", valueGetter: (value, row) => row.period.name },
      {
        field: "bulan",
        headerName: "Bulan",
        type: "number",
        renderCell: (params) => month[params.row.bulan - 1],
      },
      { field: "tahun", headerName: "Tahun" },
      {
        field: "bahanB3",
        headerName: "Bahan B3",
        valueGetter: (value, row) =>
          row?.pengangkutanDetails
            ?.map((detail) => detail.dataBahanB3?.namaBahanKimia)
            ?.filter((nama) => nama)
            ?.join(", ") || "-",
      },
      {
        field: "perusahaanAsalMuat",
        headerName: "Asal Muat",
        valueGetter: (value, row) =>
          row.pengangkutanDetails
            ?.map((detail) => detail.DataPerusahaanTujuanBongkarOnPengakutanDetail?.map((subDetail) => subDetail.perusahaanAsalMuat?.namaPerusahaan)?.filter((nama) => nama))
            .flat()
            .join(", ") || "-",
      },
      {
        field: "perusahaanTujuanBongkar",
        headerName: "Tujuan Bongkar",
        valueGetter: (value, row) =>
          row.pengangkutanDetails
            ?.map((detail) => detail.DataPerusahaanTujuanBongkarOnPengakutanDetail?.map((subDetail) => subDetail.perusahaanTujuanBongkar?.namaPerusahaan)?.filter((nama) => nama))
            .flat()
            .join(", ") || "-",
      },
      {
        field: "totalDiangkut",
        headerName: "Total Diangkut (Kg)",
        valueGetter: (value, row) => row?.pengangkutanDetails?.reduce((total, detail) => total + (detail.jumlahB3 || 0), 0) || 0,
      },
      {
        field: "isFinalized",
        headerName: "Finalisasi",
        renderCell: (params) => (
          <div className="flex mt-1">
            <IsValidIcon value={params.row.isFinalized} validMessage="Sudah Finalisasi" invalidMessage="Belum Finalisasi" />
          </div>
        ),
      },
      {
        field: "status",
        headerName: "Status",
        renderCell: (params) => {
          const { status, pengangkutanDetails, laporanTersedia } = params.row;

          // Status pengangkutan baru terisi saat finalisasi/persetujuan, jadi
          // baris yang sudah diisi datanya tetap tampil "Draft" - itu yang
          // membingungkan pengguna. Diturunkan di layar saja, kolom status di
          // database tidak diubah, supaya alur finalisasi tidak tersentuh.
          // Pola dan ambangnya disamakan dengan modul produksi, penggunaan,
          // distribusi, dan pembelian.
          let computedStatus = status;
          if (!status) {
            const adaData = pengangkutanDetails?.length > 0;
            if ((adaData && laporanTersedia === true) || (!adaData && laporanTersedia === false)) {
              computedStatus = "Disimpan";
            }
          }

          return <ReportStatusBadge status={computedStatus} />;
        },
        sortable: false,
        filterable: false,
      },
      {
        field: "action",
        headerName: "Aksi",
        renderCell: (params) => (
          <div className="flex items-center gap-1">
            <Button onPress={() => getReportTransportVehicleDetail(params.row.id)} size="sm" color="success" isIconOnly>
              <EyeIcon className="size-4" />
            </Button>
          </div>
        ),
        sortable: false,
        filterable: false,
      },
    ],
    [getReportTransportVehicleDetail]
  );

  return (
    <RootAdmin>
      <Card radius="sm">
        <CardBody>
          <Tabs
            color="primary"
            variant="underlined"
            aria-label="tabs"
            classNames={{
              tabList: "gap-6 w-full relative rounded-none p-0 border-b border-divider",
              cursor: "w-full",
              tab: "max-w-fit px-1.5 h-12",
              tabContent: "group-data-[selected=true]:font-semibold",
            }}
          >
            {isAdminOrPengelola && (
              <Tab key="1" title="Periode Aktif">
                <div className="h-[550px] mt-3">
                  <div className="flex justify-between gap-2 mb-5">
                    <div className="flex gap-2">
                      <div className="flex flex-col justify-end">
                        <ButtonGroup>
                          {/* <Button onPress={() => navigate(REPORT_TRANSPORT_RECOMENDATION_VEHICLE_ADD)} size="sm" color="primary" startContent={<PlusIcon className="size-4" />}>
                            Tambah
                          </Button> */}
                          <ModalGenerateReport setSelectedCompany={setSelectedCompany} setSelectedVehicle={setSelectedVehicle} setSelectedPeriod={setSelectedPeriod} mutate={mutateReport} />
                        </ButtonGroup>
                      </div>
                    </div>
                    <div className="w-full flex justify-end items-center gap-2">
                      <div className="w-1/4 relative z-50">
                        <FilterReactSelect defaultValue={selectedPeriod} placeholder="Cari Periode" options={dataPeriods?.data?.map((item) => ({ label: item.name, value: item.id }))} isLoading={isLoadingPeriods} setValue={setSelectedPeriod} />
                      </div>
                      <div className="w-1/4 relative z-50">
                        <FilterReactSelect
                          defaultValue={selectedCompany}
                          options={dataCompany?.data?.map((item) => ({ label: item.name, value: item.id }))}
                          isLoading={isLoadingCompany}
                          setValue={setSelectedCompany}
                          placeholder="Pilih Perusahaan"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <Card radius="sm" className="col-span-3 md:col-span-1 h-[420px]">
                      <CardHeader>Kendaraan</CardHeader>
                      <Divider />
                      <CardBody className="w-full p-3 space-y-3">
                        <Input
                          size="sm"
                          variant="bordered"
                          isClearable
                          value={vehicleQuery}
                          onValueChange={setVehicleQuery}
                          onClear={() => setVehicleQuery("")}
                          placeholder="Cari nomor polisi"
                          aria-label="Cari kendaraan berdasarkan nomor polisi"
                          startContent={<MagnifyingGlassIcon className="size-4 text-default-400" />}
                        />
                        <ScrollShadow hideScrollBar className="h-full">
                          {/* Kondisi mengikuti daftar KENDARAAN, bukan baris pelaporan -
                              kendaraan yang belum punya baris pelaporan pun harus tampil. */}
                          {!selectedCompany?.value ? (
                            <div className="h-full flex flex-row items-center justify-center border rounded-md">
                              <h3>Pilih perusahaan dahulu</h3>
                            </div>
                          ) : isLoadingKendaraan ? (
                            <div className="h-full flex flex-row items-center justify-center border rounded-md">
                              <Spinner className="mx-auto" />
                            </div>
                          ) : vehicleData.length === 0 ? (
                            <div className="h-full flex flex-row items-center justify-center border rounded-md">
                              <h3>Tidak ada kendaraan</h3>
                            </div>
                          ) : (
                            <RadioGroup value={selectedVehicle} onValueChange={setSelectedVehicle}>
                              {vehicleDataTersaring?.length === 0 ? (
                                <div className="py-6 text-center text-sm text-default-500">Nomor polisi tidak ditemukan</div>
                              ) : (
                                vehicleDataTersaring?.map((item) => (
                                  <RadioVehicleButton
                                    key={item?.id}
                                    value={item?.id}
                                    description={item}
                                    canManage={isAdminOrPengelola}
                                    deleteWarning="beserta seluruh detail pengangkutannya pada periode aktif?"
                                    onDelete={handleDeleteVehicle}
                                    noTransport={isNoTransport(item?.id)}
                                    isSavingNoTransport={savingNoTransportId === item?.id}
                                    onToggleNoTransport={handleToggleNoTransport}
                                    belumAdaPelaporan={getRowsForVehicle(item?.id).length === 0}
                                  />
                                ))
                              )}
                            </RadioGroup>
                          )}
                        </ScrollShadow>
                      </CardBody>
                    </Card>
                    <Card radius="sm" className="col-span-3 md:col-span-2 h-[420px]">
                      <CardHeader>Riwayat</CardHeader>
                      <Divider />
                      <CardBody>
                        <ClientDataGrid
                          columns={columns}
                          isLoading={isLoadingReport}
                          rows={selectedVehicle ? filteredData : []}
                          initialState={{
                            sorting: {
                              sortModel: [{ field: "bulan", sort: "asc" }],
                            },
                          }}
                        />
                      </CardBody>
                    </Card>
                  </div>
                </div>
              </Tab>
            )}
            {isAdminOrPengelola && (
              <Tab key="2" title="Preview">
                <TableReportTransportPreview
                  isPreviewOnly
                  selectedCompany={selectedCompany}
                  setSelectedCompany={setSelectedCompany}
                  selectedPeriod={selectedPeriod}
                  setSelectedPeriod={setSelectedPeriod}
                />
              </Tab>
            )}
            <Tab key="3" title="Riwayat">
              <TableHistoryTransport
                dataCompany={dataCompany}
                isLoadingCompany={isLoadingCompany}
                selectedCompany={selectedCompany}
                selectedPeriod={selectedPeriod}
                setSelectedPeriod={setSelectedPeriod}
              />
            </Tab>
          </Tabs>
        </CardBody>
      </Card>
    </RootAdmin>
  );
}